import { useState, useEffect } from 'react'
import { Button, Tag, Card, Popover, Icon, Title, Text } from '@nimbus-ds/components'
import {
  EllipsisIcon,
  PlayIcon,
  StopIcon,
  EyeIcon,
  CogIcon,
  TrashIcon,
  StatsIcon,
  ArrowupIcon,
  ArrowDownIcon,
  QuestionCircleIcon,
  ArchiveIcon,
  CheckCircleIcon,
  RepeatIcon,
} from '@nimbus-ds/icons'
import type { Experiment } from '@/types'
import { experimentService } from '@/api/experiments'

interface ResultsSummary {
  is_significant: boolean
  winner: string | null
  lift_percentage: number
  total_sessions: number
  control_rate: number
  treatment_rate: number
}

interface ExperimentsListProps {
  experiments: Experiment[]
  onExperimentAction?: (action: string, experimentId: string) => void
  loading?: boolean
  actionLoading?: string | null
}

// Radix DropdownMenu (⋮ actions) → Nimbus Popover with a stacked list of transparent buttons.
// Controlled visibility so clicking an item both fires the action and closes the menu.
function ExperimentActionsMenu({
  experiment,
  actionLoading,
  onAction,
}: {
  experiment: Experiment
  actionLoading?: string | null
  onAction: (action: string, experimentId: string) => void
}) {
  const [open, setOpen] = useState(false)

  const item = (icon: React.ReactNode, label: string, action: string) => (
    <Button appearance="transparent" onClick={() => { onAction(action, experiment.id); setOpen(false) }}>
      <Icon source={icon} color="currentColor" /> {label}
    </Button>
  )

  const content = (
    <div className="flex flex-col">
      {experiment.status === 'draft' && item(<PlayIcon />, 'Iniciar', 'start')}
      {experiment.status === 'active' && item(<StopIcon />, 'Pausar', 'pause')}
      {experiment.status === 'paused' && item(<PlayIcon />, 'Reanudar', 'resume')}
      {(experiment.status === 'active' || experiment.status === 'paused') &&
        item(<CheckCircleIcon />, 'Finalizar', 'complete')}
      {/* Repetir: exact relaunch; the source is archived (CLONE-EXPERIMENT-SPEC.md). Not for drafts. */}
      {experiment.status !== 'draft' && item(<RepeatIcon />, 'Repetir', 'repeat')}
      {item(<EyeIcon />, 'Ver Detalles', 'view')}
      {item(<StatsIcon />, 'Ver Resultados', 'results')}
      {item(<CogIcon />, 'Editar', 'edit')}
      {experiment.status === 'completed' && item(<ArchiveIcon />, 'Archivar', 'archive')}
      {item(<TrashIcon />, 'Eliminar', 'delete')}
    </div>
  )

  return (
    <Popover
      content={content}
      visible={open}
      onVisibility={setOpen}
      arrow={false}
      position="bottom-end"
      padding="small"
    >
      <Button appearance="transparent" disabled={actionLoading === experiment.id}>
        <Icon source={<EllipsisIcon />} color="currentColor" />
      </Button>
    </Popover>
  )
}

/**
 * The traffic split, read off the row instead of guessed.
 *
 * This line used to print `traffic_split?.treatment || 50`, which silently invented "50%" for
 * anything without a `treatment` key — i.e. every multi-variant experiment AND every optimization
 * slot, whose split is rewritten daily by the allocator and is the single most informative number
 * about it. Now it renders each arm's real weight, and for a slot adds when the allocator last
 * moved it, so a consultant can tell "adjusting itself" from "stuck".
 */
function TrafficSplitLine({ experiment }: { experiment: Experiment }) {
  const split = experiment.traffic_split || {}
  const arms = Object.keys(split).sort((a, b) =>
    a === 'control' ? -1 : b === 'control' ? 1 : a.localeCompare(b)
  )
  if (arms.length === 0) return <Text>Traffic Split: —</Text>

  const isSlot = experiment.mode === 'optimization'
  const updated = experiment.allocation?.updated_at

  return (
    <Text>
      {isSlot ? 'Asignación actual: ' : 'Traffic Split: '}
      {arms.map((arm) => `${arm} ${split[arm] ?? 0}%`).join(' · ')}
      {isSlot && updated
        ? ` · recalculada el ${new Date(updated).toLocaleDateString('es-ES', { dateStyle: 'short' })}`
        : ''}
    </Text>
  )
}

export function ExperimentsList({
  experiments,
  onExperimentAction,
  loading,
  actionLoading,
}: ExperimentsListProps) {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null)
  const [resultsSummaries, setResultsSummaries] = useState<Record<string, ResultsSummary>>({})

  useEffect(() => {
    const finishedExps = experiments.filter(e => e.status === 'completed' || e.status === 'archived')
    if (finishedExps.length === 0) return

    // Use frozen results_summary if available, otherwise fetch from API
    const needsFetch: typeof finishedExps = []

    for (const exp of finishedExps) {
      if (exp.results_summary) {
        const s = exp.results_summary!
        setResultsSummaries(prev => ({
          ...prev,
          [exp.id]: {
            is_significant: s.is_significant,
            winner: s.winner ?? null,
            lift_percentage: s.lift?.percentage ?? 0,
            total_sessions: s.total_sessions ?? 0,
            control_rate: s.variants?.control?.conversion_rate ?? 0,
            treatment_rate: s.variants?.treatment?.conversion_rate ?? 0,
          }
        }))
      } else {
        needsFetch.push(exp)
      }
    }

    // Fallback: fetch from results API for experiments not yet finalized
    needsFetch.forEach(exp => {
      experimentService.getExperimentResults(exp.id).then(results => {
        if (!results?.summary) return
        setResultsSummaries(prev => ({
          ...prev,
          [exp.id]: {
            is_significant: results.summary.is_significant,
            winner: results.summary.winner,
            lift_percentage: results.summary.lift?.percentage ?? 0,
            total_sessions: results.summary.total_sessions ?? 0,
            control_rate: results.conversion_rate?.control ?? 0,
            treatment_rate: results.conversion_rate?.treatment ?? 0,
          }
        }))
      }).catch(() => {})
    })
  }, [experiments])

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <Card>
              <Card.Header>
                <div className="h-4 w-1/3 rounded bg-muted"></div>
                <div className="h-3 w-1/2 rounded bg-muted"></div>
              </Card.Header>
              <Card.Body>
                <div className="h-16 rounded bg-muted"></div>
              </Card.Body>
            </Card>
          </div>
        ))}
      </div>
    )
  }

  if (experiments.length === 0) {
    return (
      <Card>
        <Card.Body>
          <div className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-muted">
              <div className="text-muted-foreground">
                <Icon source={<StatsIcon />} color="currentColor" />
              </div>
            </div>
            <Title as="h3">No hay experimentos</Title>
            <Text>
              Comienza creando tu primer experimento A/B para optimizar tu
              tienda.
            </Text>
          </div>
        </Card.Body>
      </Card>
    )
  }

  const getStatusBadge = (status: Experiment['status']) => {
    const appearances = {
      draft: 'neutral',
      active: 'primary',
      paused: 'neutral',
      completed: 'danger',
      archived: 'neutral',
    } as const

    const labels = {
      draft: 'Borrador',
      active: 'Activo',
      paused: 'Pausado',
      completed: 'Completado',
      archived: 'Archivado',
    }

    return <Tag appearance={appearances[status]}>{labels[status]}</Tag>
  }

  const getTierBadge = (tier: number | null) => {
    // Handle null/undefined tier values
    if (!tier) {
      return <Tag appearance="neutral">Tier - Sin configurar</Tag>
    }

    const appearances = {
      1: 'success',
      2: 'warning',
      3: 'danger',
    } as const

    const labels = {
      1: 'No-code',
      2: 'Low-code',
      3: 'Custom',
    }

    return (
      <Tag appearance={appearances[tier as keyof typeof appearances]}>
        Tier {tier} - {labels[tier as keyof typeof labels]}
      </Tag>
    )
  }

  const handleAction = (action: string, experimentId: string) => {
    if (onExperimentAction) {
      onExperimentAction(action, experimentId)
    }
  }

  return (
    <div className="space-y-4">
      {experiments.map((experiment) => (
        <div
          key={experiment.id}
          className={`transition-all duration-200 ${
            hoveredCard === experiment.id ? 'border-[var(--nimbus-colors-primary-surface)] shadow-md' : ''
          }`}
          onMouseEnter={() => setHoveredCard(experiment.id)}
          onMouseLeave={() => setHoveredCard(null)}
        >
          <Card>
            <Card.Header>
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Title as="h3">{experiment.name}</Title>
                    {getStatusBadge(experiment.status)}
                    {getTierBadge(experiment.tier)}
                    {/* Optimizations are experiment rows but a different family: they allocate
                        instead of concluding, so no verdict or SRM will ever appear on them
                        (OPTIMIZATIONS-SPEC.md §6). Without this tag they read as a test stuck at
                        50/50 forever. */}
                    {experiment.mode === 'optimization' && (
                      <Tag appearance="primary">Optimización</Tag>
                    )}
                    {/* EXP_TIMING_001: Timing & Session Badges (hide for finished experiments) */}
                    {experiment.status !== 'completed' && experiment.status !== 'archived' && (
                      <>
                        {experiment.timing?.start_date_time && new Date(experiment.timing.start_date_time) > new Date() && (
                          <Tag appearance="primary">
                            Programado: {new Date(experiment.timing.start_date_time).toLocaleString('es-ES', {
                              dateStyle: 'short',
                              timeStyle: 'short'
                            })}
                          </Tag>
                        )}
                        {experiment.timing?.end_date_time && (
                          <Tag appearance="warning">
                            Finaliza: {new Date(experiment.timing.end_date_time).toLocaleString('es-ES', {
                              dateStyle: 'short',
                              timeStyle: 'short'
                            })}
                          </Tag>
                        )}
                        {experiment.session_limit && (
                          <Tag appearance="neutral">
                            Sesiones: {experiment.current_sessions || 0} / {experiment.session_limit.toLocaleString('es-ES')}
                          </Tag>
                        )}
                      </>
                    )}
                  </div>
                  <Text>{experiment.objective}</Text>
                </div>

                <ExperimentActionsMenu
                  experiment={experiment}
                  actionLoading={actionLoading}
                  onAction={handleAction}
                />
              </div>
            </Card.Header>
            <Card.Body>
              <div className="space-y-3">
                {(experiment.status === 'completed' || experiment.status === 'archived') && resultsSummaries[experiment.id] ? (() => {
                  const r = resultsSummaries[experiment.id]
                  const isNegative = experiment.is_negative === true
                  const conclusive = r.is_significant

                  // Hypothesis variant: treatment by default, control for negative experiments.
                  const hypothesisWinner = isNegative ? 'control' : 'treatment'
                  const baselineWinner = isNegative ? 'treatment' : 'control'

                  const hypothesisWon = conclusive && r.winner === hypothesisWinner
                  const hypothesisLost = conclusive && r.winner === baselineWinner

                  // Lift framed from the hypothesis variant's perspective:
                  // (hypothesis_rate - baseline_rate) / baseline_rate.
                  // Falls back to lambda lift_percentage for normal experiments.
                  let hypothesisLift = r.lift_percentage
                  if (isNegative) {
                    hypothesisLift = r.treatment_rate > 0
                      ? ((r.control_rate - r.treatment_rate) / r.treatment_rate) * 100
                      : -r.lift_percentage
                  }

                  return (
                    <div className={`rounded-lg px-4 py-3 ${
                      hypothesisWon ? 'bg-green-50 border border-green-200' :
                      hypothesisLost ? 'bg-red-50 border border-red-200' :
                      'bg-amber-50 border border-amber-200'
                    }`}>
                      <div className="flex items-start gap-3">
                        {hypothesisWon ? <div className="text-green-600 mt-0.5 shrink-0"><Icon source={<ArrowupIcon />} color="currentColor" /></div> :
                         hypothesisLost ? <div className="text-red-600 mt-0.5 shrink-0"><Icon source={<ArrowDownIcon />} color="currentColor" /></div> :
                         <div className="text-amber-600 mt-0.5 shrink-0"><Icon source={<QuestionCircleIcon />} color="currentColor" /></div>}
                        <div className="space-y-1">
                          <p className={`text-sm font-semibold ${
                            hypothesisWon ? 'text-green-800' :
                            hypothesisLost ? 'text-red-800' :
                            'text-amber-800'
                          }`}>
                            {hypothesisWon
                              ? (isNegative
                                ? `Hipótesis confirmada (+${hypothesisLift.toFixed(1)}% lift)`
                                : `Tratamiento ganador (+${hypothesisLift.toFixed(1)}% lift)`)
                              : hypothesisLost
                                ? (isNegative
                                  ? `Hipótesis refutada (${hypothesisLift.toFixed(1)}% lift)`
                                  : `Control ganador (${hypothesisLift.toFixed(1)}% lift)`)
                                : 'No concluyente'}
                          </p>
                          <p className={`text-sm ${
                            hypothesisWon ? 'text-green-700' :
                            hypothesisLost ? 'text-red-700' :
                            'text-amber-700'
                          }`}>
                            {hypothesisWon
                              ? (isNegative
                                ? 'El cambio testeado funciona: implementarlo como versión principal.'
                                : 'Aplicar la variante de tratamiento de forma permanente.')
                              : hypothesisLost
                                ? (isNegative
                                  ? 'Mantener la versión actual: el cambio testeado empeora los resultados.'
                                  : 'Descartar el tratamiento y mantener el control.')
                                : 'Resultados sin significancia estadística. Considerar extender la prueba o rediseñar.'}
                          </p>
                          <Text fontSize="caption">
                            {r.total_sessions.toLocaleString()} sesiones
                          </Text>
                        </div>
                      </div>
                    </div>
                  )
                })() : (
                  <>
                    <div>
                      <Text fontSize="caption">HIPÓTESIS</Text>
                      <Text>{experiment.hypothesis}</Text>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Text as="span">
                          <Text as="span" color="neutral-textHigh">Tipo:</Text> {experiment.type}
                        </Text>
                        <Text as="span">
                          <Text as="span" color="neutral-textHigh">Métrica:</Text>{' '}
                          {experiment.goals?.primary?.label || experiment.primary_metric || '—'}
                        </Text>
                      </div>
                      <Text fontSize="caption">
                        Creado{' '}
                        {new Date(experiment.created_at).toLocaleDateString('es-ES')}
                      </Text>
                    </div>

                    <div className="flex items-center justify-between border-t pt-2">
                      <TrafficSplitLine experiment={experiment} />
                      {experiment.status === 'draft' && (
                        <Tag appearance="neutral">
                          Setup incompleto
                        </Tag>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Card.Body>
          </Card>
        </div>
      ))}
    </div>
  )
}
